import React, { useState, useRef, useEffect } from 'react';
import { Edit2, Trash2, CheckSquare, Square, Star, Sparkles, Copy, AlertTriangle, HeartHandshake, ArrowUpCircle, Filter, X, Calendar } from 'lucide-react';
import {
  NATURES, INGREDIENT_ICONS, MAIN_SKILL_ICONS,
  getSkillColor, HYBRID_ROLES, formatSkillName, formatIngredientName,
  BERRY_TO_TYPE, TYPE_ICONS, BERRY_ICONS, SUBSKILL_ABBREVIATIONS
} from '../utils/gameData';

export default function PokemonCard({ 
  mon, 
  pokedex, 
  densityLevel = 10, 
  isSelected, 
  isRecentlyAdded,
  onSelect, 
  onEdit, 
  onDelete,
  onSave,
  onClone,
  onLevelUp,
  activeFilters = {},
  onFilterBy,
  onClearFilters
}) {
  // --- Filter Menu Logic ---
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ goesUp: true, goesRight: false });
  const filterMenuRef = useRef(null);
  
  const handleToggleFilterMenu = (e) => {
      e.stopPropagation();
      if (!isFilterMenuOpen) {
          const rect = e.currentTarget.getBoundingClientRect();
          setPopoverPos({
              goesUp: rect.top > window.innerHeight / 2,
              goesRight: rect.left < window.innerWidth / 2
          });
      }
      setIsFilterMenuOpen(!isFilterMenuOpen);
  };

  useEffect(() => {
      function handleClickOutside(event) {
          if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) setIsFilterMenuOpen(false);
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const species = pokedex[mon.species_id];
  if (!species) return null;

  const activeSkill = mon.custom_skill || species.skill;
  const mainSkillIcon = MAIN_SKILL_ICONS[activeSkill] || "/images/mainskill/metronome.png";
  const skillName = formatSkillName(activeSkill);

  // --- Logic: Check for Special Roles & Bonuses ---
  const unlockLevels = [10, 25, 50, 75, 100];
  const hasHelpingBonus = mon.subskills.some((s, i) => s === 'Helping Bonus' && mon.level >= unlockLevels[i]);
  const hybridRole = HYBRID_ROLES[species.id];

  // --- Shiny Image Logic ---
  const imagePath = `/images/pokemon/${species.id.toLowerCase()}${mon.is_shiny ? '_shiny' : ''}.png`;

  // --- Toggle Favorite ---
  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    onSave({ ...mon, is_favorite: !mon.is_favorite });
  };

  // --- Clone Handler ---
  const handleClone = (e) => {
    e.stopPropagation();
    onClone(mon);
  }

  // --- SAFE DELETE ---
  const handleDelete = (e) => {
    e.stopPropagation();
    if (mon.is_favorite) return;

    if (hasHelpingBonus) {
        const confirmed = window.confirm(
            "⚠️ WAIT! This Pokémon has 'Helping Bonus'.\n\n" +
            "This rare subskill boosts your WHOLE team's speed by 5%.\n" +
            "Are you absolutely sure you want to release it?"
        );
        if (!confirmed) return;
    }
    
    onDelete(mon.uuid);
  };

  const renderChip = (label, type, value, isActive, keySuffix = '') => (
      <button
          key={`${type}-${value}-${keySuffix}`}
          onClick={(e) => { e.stopPropagation(); onFilterBy({ type, value }); }}
          className={`px-2 py-1 text-[10px] font-bold rounded-lg border transition-colors flex items-center gap-1 w-max ${isActive ? 'bg-indigo-100 text-indigo-700 border-indigo-300 shadow-inner' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 shadow-sm'}`}
      >
          {label}
      </button>
  );

  const filterMenu = (
      <div className="relative flex items-center">
        <button onClick={handleToggleFilterMenu} className={`p-1.5 rounded-lg transition-colors ${isFilterMenuOpen ? 'text-indigo-600 bg-indigo-50' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`} title="Filter by this Pokémon">
          <Filter size={16}/>
        </button>
        
        {isFilterMenuOpen && (
          <>
            <div className="fixed inset-0 z-40 md:hidden bg-black/20 backdrop-blur-[2px]" onClick={(e) => { e.stopPropagation(); setIsFilterMenuOpen(false); }}></div>
            <div ref={filterMenuRef} className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:absolute md:translate-x-0 md:translate-y-0 w-[90vw] md:w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[75vh] flex flex-col overflow-hidden cursor-default ${popoverPos.goesUp ? 'md:bottom-[calc(100%+12px)] md:top-auto' : 'md:top-[calc(100%+12px)] md:bottom-auto'} ${popoverPos.goesRight ? 'md:left-0 md:right-auto' : 'md:right-0 md:left-auto'}`} onClick={(e) => e.stopPropagation()}>
              <div className="bg-gray-800 p-3 flex justify-between items-center text-white shrink-0 shadow-md z-10">
                <h4 className="font-black text-sm flex items-center gap-1.5"><Filter size={16} className="text-indigo-400" /> Filter Similar</h4>
                <div className="flex items-center gap-2">
                  {onClearFilters && (
                    <button onClick={(e) => { e.stopPropagation(); onClearFilters(); setIsFilterMenuOpen(false); }} className="text-[10px] font-bold text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded transition shadow-sm">Reset All</button>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); setIsFilterMenuOpen(false); }} className="text-gray-400 hover:text-white p-1 bg-gray-700 hover:bg-red-500 rounded-md transition shadow-sm"><X size={14}/></button>
                </div>
              </div>
              
              <div className="p-4 flex flex-col gap-2.5 overflow-y-auto custom-scrollbar">
                <div>
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">General & Traits</div>
                  <div className="flex flex-wrap gap-1.5">
                    {renderChip(`Species: ${species.name}`, 'search', species.name, activeFilters?.searchTerm === species.name)}
                    {species.family && renderChip(`Family: ${species.family}`, 'search', species.family, activeFilters?.searchTerm === species.family)}
                    {renderChip(`Specialty: ${species.specialty}`, 'specialty', species.specialty, activeFilters?.specialty?.includes(species.specialty))}
                    {renderChip(<><span className="text-[12px] leading-none">{TYPE_ICONS[BERRY_TO_TYPE[species.berry]]}</span> {BERRY_TO_TYPE[species.berry] || 'Unknown'}</>, 'type', BERRY_TO_TYPE[species.berry], activeFilters?.pokemonType?.includes(BERRY_TO_TYPE[species.berry]))}
                    {renderChip(<><img src={BERRY_ICONS[species.berry]} className="w-3 h-3" alt=""/> {species.berry}</>, 'berry', species.berry, activeFilters?.berry?.includes(species.berry))}
                    {renderChip(<><img src={mainSkillIcon} className="w-3 h-3" alt=""/> {skillName}</>, 'mainSkill', activeSkill, activeFilters?.mainSkill?.includes(activeSkill))}
                    {renderChip(`Nature: ${mon.nature}`, 'nature', mon.nature, activeFilters?.nature?.up === (NATURES[mon.nature]?.up || 'None') && activeFilters?.nature?.down === (NATURES[mon.nature]?.down || 'None'))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 border-t border-gray-100 pt-2">Ingredients (Unlocked)</div>
                  <div className="flex flex-wrap gap-1.5">
                    {[...new Set(["0", "30", "60"].filter(lvl => mon.level >= parseInt(lvl) && mon.ingredients[lvl]).map(lvl => mon.ingredients[lvl].name))].map((ingName, idx) => (
                      renderChip(<><img src={INGREDIENT_ICONS[ingName]} className="w-3 h-3" alt=""/> {formatIngredientName ? formatIngredientName(ingName) : ingName.replace(/_/g, ' ')}</>, 'ingredient', ingName, activeFilters?.ingredients?.includes(ingName), idx)
                    ))}
                  </div>
                </div>
                {(() => {
                  const lockedIngs = [...new Set(["0", "30", "60"].filter(lvl => mon.level < parseInt(lvl) && mon.ingredients[lvl]).map(lvl => mon.ingredients[lvl].name))];
                  if (lockedIngs.length === 0) return null;
                  return (
                    <div>
                      <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 border-t border-gray-100 pt-2">Ingredients (Locked)</div>
                      <div className="flex flex-wrap gap-1.5">
                        {lockedIngs.map((ingName, idx) => (
                          renderChip(<><img src={INGREDIENT_ICONS[ingName]} className="w-3 h-3 opacity-70" alt=""/> <span className="opacity-70">{formatIngredientName ? formatIngredientName(ingName) : ingName.replace(/_/g, ' ')}</span></>, 'ingredient', ingName, activeFilters?.ingredients?.includes(ingName), `locked-${idx}`)
                        ))}
                      </div>
                    </div>
                  );
                })()}
                <div>
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 border-t border-gray-100 pt-2">Sub-skills</div>
                  <div className="flex flex-wrap gap-1.5">
                    {mon.subskills.filter(Boolean).map((skill, idx) => (
                      renderChip(skill, 'subskill', skill, activeFilters?.subskills?.includes(skill), idx)
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
  );

  // TEMP: flip to false to preview Layout B (horizontal) for Level 3
  const L3_PORTRAIT = true;

  // Density Logic (4 levels)
  const showZone3 = densityLevel >= 2; // production: berries + ingredients
  const showZone4 = densityLevel >= 3; // abilities: main skill + subskills
  const useAbbreviations = densityLevel < 4;
  const showZone5 = densityLevel >= 4; // character: nature, date, badges

  // 4 tiers, one per level
  const tier = densityLevel - 1; // 0, 1, 2, 3
  // Level 4 uses vertical/portrait layout
  const isVertical = tier === 3;

  const pClass          = ['p-0.5',               'p-1',              'p-1.5',            'p-2.5'           ][tier];
  const gapClass        = ['gap-1',                'gap-1.5',          'gap-2',            'gap-3'           ][tier];
  const headerPadClass  = ['pl-2.5 pr-1.5 py-0.5', 'px-2 py-1',       'px-3 py-1.5',      'px-3 py-2'       ][tier];
  const headerNameClass = ['text-[9px]',           'text-xs',          'text-sm',          'text-sm'         ][tier];
  const spriteWrapClass = ['w-8 h-8 rounded',      'w-12 h-12 rounded-lg', 'w-14 h-14 rounded-xl', 'w-24 h-24 rounded-2xl'][tier];
  const spriteImgClass  = ['w-6 h-6',              'w-10 h-10',        'w-12 h-12',        'w-20 h-20'       ][tier];
  const titleClass      = ['text-[10px]',          'text-xs',          'text-sm',          'text-base'       ][tier];
  const badgeTextClass  = ['text-[8px]',           'text-[9px]',       'text-[10px]',      'text-xs'         ][tier];
  const iconClass       = ['w-2.5 h-2.5',          'w-3 h-3',          'w-4 h-4',          'w-5 h-5'         ][tier];
  const smallIconClass  = ['w-2 h-2',              'w-2.5 h-2.5',      'w-3 h-3',          'w-3.5 h-3.5'     ][tier];
  const subskillHeightClass = ['h-[14px]',         'h-[16px]',         'h-[18px]',         'h-[22px]'        ][tier];

  return (
    <div
      onClick={onSelect}
      className={`bg-white rounded-xl shadow-sm border overflow-hidden relative flex flex-col cursor-pointer transition-all hover:shadow-md h-full
        ${isSelected ? 'ring-2 ring-indigo-500 border-transparent' : 'border-gray-200'}
      `}
    >
      {/* Header Bar */}
      <div className={`${tier === 0 ? 'bg-purple-100 border-b border-purple-200' : 'bg-purple-50 border-b border-purple-100'} ${headerPadClass} flex justify-between items-center w-full z-20 shrink-0`}>
        <div className="flex flex-col min-w-0 flex-1 pr-2">
          <span className={`${tier === 0 ? 'font-black' : 'font-bold'} ${headerNameClass} text-purple-900 truncate leading-tight`}>
            {mon.nickname || species.name}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={(e) => { e.stopPropagation(); onSelect(); }} className="text-purple-300 hover:text-purple-600 transition-colors flex items-center justify-center">
              {isSelected ? <CheckSquare size={18} className="text-indigo-500 bg-white rounded-sm"/> : <Square size={18} className="bg-white/50 rounded-sm"/>}
          </button>
        </div>
      </div>

      {/* Zone 1 & 2 */}
      {(tier <= 1 || (tier === 2 && L3_PORTRAIT) || tier === 3) ? (
        /* Level 1, 2, portrait Level 3, and Level 4: sprite fills card width */
        <div className={`${pClass} flex flex-col items-center gap-1`}>
          <div className={`relative w-full ${tier === 0 ? 'aspect-square' : tier === 1 ? 'h-[130px]' : tier === 2 ? 'h-[100px]' : 'h-[150px]'} bg-gradient-to-br from-gray-50 to-white border border-gray-100 shadow-sm rounded-lg flex items-center justify-center`}>
            <img
                src={imagePath}
                alt={species.name}
                className="w-4/5 h-4/5 object-contain drop-shadow-md relative z-0"
                onError={(e) => { e.target.onerror = null; e.target.src = "/images/pokemon/unknown.png" }}
            />
            <div className={`absolute top-1.5 left-1.5 rounded px-1.5 py-1 shadow-sm z-10 flex items-center justify-center ${species.specialty === 'berry' ? 'bg-green-100' : species.specialty === 'ingredient' ? 'bg-orange-100' : species.specialty === 'all' ? 'bg-purple-100' : 'bg-blue-100'}`}>
              <span className={`text-[10px] leading-none font-black uppercase tracking-tight block ${species.specialty === 'berry' ? 'text-green-700' : species.specialty === 'ingredient' ? 'text-orange-600' : species.specialty === 'all' ? 'text-purple-700' : 'text-blue-600'}`}>
                {tier === 0 ? species.specialty.substring(0, 3) : species.specialty}
              </span>
            </div>
            {mon.is_shiny && (
              <div className="absolute top-1.5 right-1.5 bg-white/90 backdrop-blur-[2px] rounded border border-gray-200 px-1.5 py-1 shadow-sm z-10 flex items-center justify-center">
                <Sparkles size={12} className="text-indigo-600 fill-indigo-100" />
              </div>
            )}
            <button
              onClick={handleToggleFavorite}
              className="absolute bottom-1.5 left-1.5 bg-white/90 backdrop-blur-[2px] rounded-full p-1 shadow-sm border border-gray-200 hover:scale-110 transition-transform z-10"
              title={mon.is_favorite ? "Unfavorite" : "Mark as Favorite"}
            >
              <Star size={10} className={mon.is_favorite ? "fill-yellow-400 text-yellow-400" : "text-gray-400 hover:text-yellow-400"} />
            </button>
            <div className="absolute bottom-1.5 right-1.5 bg-indigo-100 px-1.5 py-1 rounded shadow-sm z-10 flex items-center justify-center">
              <span className="text-[10px] leading-none font-black text-indigo-700">Lv.{mon.level}</span>
            </div>
          </div>
        </div>
      ) : tier === 2 ? (
        /* Layout B: L3 horizontal — sprite thumbnail left, production + abilities right */
        <div className="flex gap-2 p-2 items-start">
          {/* Sprite thumbnail with badges */}
          <div className="relative shrink-0 w-[68px] h-[68px] bg-gradient-to-br from-gray-50 to-white border border-gray-100 shadow-sm rounded-xl flex items-center justify-center">
            <img
              src={imagePath}
              alt={species.name}
              className="w-[56px] h-[56px] object-contain drop-shadow-md relative z-0"
              onError={(e) => { e.target.onerror = null; e.target.src = "/images/pokemon/unknown.png" }}
            />
            <div className={`absolute top-1 left-1 rounded px-1 py-0.5 shadow-sm z-10 ${species.specialty === 'berry' ? 'bg-green-100' : species.specialty === 'ingredient' ? 'bg-orange-100' : species.specialty === 'all' ? 'bg-purple-100' : 'bg-blue-100'}`}>
              <span className={`text-[7px] leading-none font-black uppercase tracking-tight block ${species.specialty === 'berry' ? 'text-green-700' : species.specialty === 'ingredient' ? 'text-orange-600' : species.specialty === 'all' ? 'text-purple-700' : 'text-blue-600'}`}>
                {species.specialty}
              </span>
            </div>
            {mon.is_shiny && (
              <div className="absolute top-1 right-1 bg-white/90 backdrop-blur-[2px] rounded-full p-0.5 shadow-sm border border-gray-200 z-10">
                <Sparkles size={8} className="text-indigo-600 fill-indigo-100" />
              </div>
            )}
            <button
              onClick={handleToggleFavorite}
              className="absolute -bottom-1 -left-1 bg-white/90 backdrop-blur-[2px] rounded-full p-1 shadow-sm border border-gray-200 hover:scale-110 transition-transform z-10"
              title={mon.is_favorite ? "Unfavorite" : "Mark as Favorite"}
            >
              <Star size={10} className={mon.is_favorite ? "fill-yellow-400 text-yellow-400" : "text-gray-400 hover:text-yellow-400"} />
            </button>
            <div className="absolute bottom-1 right-1 bg-indigo-100 px-1 py-0.5 rounded shadow-sm z-10">
              <span className="text-[8px] leading-none font-black text-indigo-700">Lv.{mon.level}</span>
            </div>
          </div>
          {/* Right column: Production + Abilities */}
          <div className="flex flex-col flex-1 min-w-0 gap-1.5">
            {/* Production subgroup */}
            <div className="flex items-center bg-gray-50 border border-gray-100 rounded-lg p-1.5 gap-1.5">
              <div className="flex items-center gap-1 bg-white border border-gray-200 rounded px-1 py-0.5 shadow-sm shrink-0">
                <img src={BERRY_ICONS[species.berry]} className="w-2.5 h-2.5 object-contain shrink-0" alt=""/>
                <span className="text-[9px] font-bold text-gray-600 truncate">{BERRY_TO_TYPE[species.berry] || '?'}</span>
              </div>
              <div className="w-px self-stretch bg-gray-200 shrink-0"/>
              <div className="flex items-center gap-1 flex-1 justify-around">
                {["0", "30", "60"].map((lvl) => {
                  const ing = mon.ingredients[lvl];
                  if (!ing) return null;
                  const isLocked = mon.level < parseInt(lvl);
                  return (
                    <div key={lvl} className={`flex items-center gap-0.5 ${isLocked ? 'opacity-60' : ''}`} title={`Lv.${lvl}`}>
                      <img src={INGREDIENT_ICONS[ing.name]} className="w-3 h-3 object-contain" alt=""/>
                      <span className="text-[9px] font-black text-gray-600">x{ing.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Abilities subgroup */}
            <div className="flex items-center justify-between bg-indigo-50 border border-indigo-100 rounded-lg p-1 px-1.5 shadow-inner">
              <div className="flex items-center gap-1 min-w-0">
                <img src={mainSkillIcon} className="w-3.5 h-3.5 object-contain drop-shadow-sm shrink-0" alt=""/>
                <span className="text-[9px] font-bold text-indigo-900 truncate">{skillName}</span>
              </div>
              <span className="text-[9px] font-black text-indigo-500 bg-white px-1 py-0.5 rounded shadow-sm shrink-0 border border-indigo-100">Lv.{mon.main_skill_level || 1}</span>
            </div>
            <div className="grid grid-cols-2 gap-1">
              {mon.subskills.map((skill, i) => {
                if (!skill) return <div key={i} className="w-full h-[16px] bg-gray-50 rounded-[4px] border border-gray-100 shadow-inner"/>;
                const isUnlocked = mon.level >= unlockLevels[i];
                const colorClass = getSkillColor(skill);
                return (
                  <div key={i} className={`w-full h-[16px] flex items-center justify-center rounded-[4px] border text-[9px] font-black tracking-tight shadow-sm truncate px-1 ${colorClass} ${!isUnlocked ? 'opacity-60' : ''}`} title={skill}>
                    {SUBSKILL_ABBREVIATIONS[skill] || skill}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {/* Zone 3: Berries & Ingredients */}
      {showZone3 && (tier !== 2 || L3_PORTRAIT) && (
        <div className={`px-2 pb-2`}>
          <div className={`flex items-center w-full bg-gray-50 border border-gray-100 rounded-lg p-1.5 gap-2`}>
            {/* Berry subgroup */}
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded px-1.5 py-0.5 shadow-sm shrink-0">
              <img src={BERRY_ICONS[species.berry]} className={`${smallIconClass} object-contain shrink-0`} alt=""/>
              <span className={`${badgeTextClass} font-bold text-gray-600 truncate`}>{BERRY_TO_TYPE[species.berry] || '?'}</span>
            </div>
            {/* Separator */}
            <div className="w-px self-stretch bg-gray-200 shrink-0"/>
            {/* Ingredients subgroup */}
            <div className="flex items-center gap-2 flex-1 justify-around">
              {["0", "30", "60"].map((lvl) => {
                const ing = mon.ingredients[lvl];
                if (!ing) return null;
                const isLocked = mon.level < parseInt(lvl);
                return (
                  <div key={lvl} className={`flex items-center gap-1 ${isLocked ? 'opacity-60' : ''}`} title={`Lv.${lvl}`}>
                    <img src={INGREDIENT_ICONS[ing.name]} className={`${iconClass} object-contain`} alt=""/>
                    <span className={`${badgeTextClass} font-black text-gray-600`}>x{ing.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Zone 4: Skills */}
      {showZone4 && (tier !== 2 || L3_PORTRAIT) && (
        <div className={`px-2 pb-2 flex flex-col gap-1.5`}>
          <div className="flex items-center justify-between bg-indigo-50 border border-indigo-100 rounded-lg p-1.5 px-2 shadow-inner">
            <div className="flex items-center gap-1.5 min-w-0">
              <img src={mainSkillIcon} className={`${iconClass} object-contain drop-shadow-sm shrink-0`} alt=""/>
              <span className={`${badgeTextClass} font-bold text-indigo-900 truncate`}>{skillName}</span>
            </div>
            <span className={`${badgeTextClass} font-black text-indigo-500 bg-white px-1 py-0.5 rounded shadow-sm shrink-0 border border-indigo-100`}>Lv.{mon.main_skill_level || 1}</span>
          </div>
          
          <div className="grid grid-cols-2 gap-1">
            {mon.subskills.map((skill, i) => {
              if (!skill) return <div key={i} className={`w-full ${subskillHeightClass} bg-gray-50 rounded-[4px] border border-gray-100 shadow-inner`} />;
              
              const isUnlocked = mon.level >= unlockLevels[i];
              const colorClass = getSkillColor(skill);
              
              return (
                  <div key={i} className={`w-full ${subskillHeightClass} flex items-center justify-center rounded-[4px] border ${badgeTextClass} font-black tracking-tight shadow-sm truncate px-1 ${colorClass} ${!isUnlocked ? 'opacity-60' : ''}`} title={skill}>
                      {useAbbreviations ? (SUBSKILL_ABBREVIATIONS[skill] || skill) : skill}
                  </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Zone 5: Nature, Date Met, Badges — horizontal band */}
      {showZone5 && (
        <div className="mx-2 mb-2 border-t border-gray-100 pt-1.5 flex items-center gap-2">
          {/* Nature + modifiers: left, all on one line */}
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-[10px] font-black text-gray-600 uppercase tracking-tight">{mon.nature}</span>
            {NATURES[mon.nature]?.up ? (
              <>
                <span className="text-[10px] font-bold text-red-500">+{NATURES[mon.nature].up}</span>
                <span className="text-[10px] font-bold text-blue-500">-{NATURES[mon.nature].down}</span>
              </>
            ) : (
              <span className="text-[10px] font-bold text-gray-400">Neutral</span>
            )}
          </div>
          {/* Special badges: middle */}
          <div className="flex items-center gap-1 flex-1">
            {hasHelpingBonus && (
              <span className="flex items-center gap-1 text-[8px] font-black px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 border border-yellow-200 shadow-sm tracking-wider" title="Unlocks Team Speed Bonus (+5%)">
                <HeartHandshake size={9} /> TEAM
              </span>
            )}
            {hybridRole && (
              <span className="flex items-center gap-1 text-[8px] font-black px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200 shadow-sm tracking-wider" title={hybridRole.desc}>
                <AlertTriangle size={9} /> {hybridRole.label.toUpperCase()}
              </span>
            )}
          </div>
          {/* Date: right */}
          {mon.date_met && (
            <div className="flex items-center gap-1 text-[10px] font-bold text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100 shadow-inner shrink-0">
              <Calendar size={12} />
              {new Date(mon.date_met + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
          )}
        </div>
      )}

      {/* Bottom Bar */}
      <div className={`${tier === 0 ? 'bg-gray-100 border-t border-gray-200 py-0.5 px-1' : 'bg-gray-50 border-t border-gray-200 p-1'} flex items-center justify-around w-full shrink-0 mt-auto`}>
        {filterMenu}
        <button onClick={handleClone} className={`transition-colors rounded-lg ${tier === 0 ? 'p-0.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50' : 'p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`} title="Clone">
          {tier === 0 ? <Copy size={12}/> : <Copy size={16}/>}
        </button>
        <button onClick={(e) => { e.stopPropagation(); onLevelUp(mon); }} className={`transition-colors rounded-lg ${tier === 0 ? 'p-0.5 text-gray-400 hover:text-green-600 hover:bg-green-50' : 'p-1 text-gray-400 hover:text-green-600 hover:bg-green-50'}`} title="Level Up Simulator">
          {tier === 0 ? <ArrowUpCircle size={12}/> : <ArrowUpCircle size={16}/>}
        </button>
        <button onClick={(e) => { e.stopPropagation(); onEdit(mon); }} className={`transition-colors rounded-lg ${tier === 0 ? 'p-0.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50' : 'p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`} title="Edit">
          {tier === 0 ? <Edit2 size={12}/> : <Edit2 size={16}/>}
        </button>
        <button
          onClick={handleDelete}
          disabled={mon.is_favorite}
          className={`rounded-lg transition-colors ${tier === 0 ? 'p-0.5' : 'p-1'} ${mon.is_favorite ? (tier === 0 ? 'text-gray-200 cursor-not-allowed' : 'text-gray-200 cursor-not-allowed') : (tier === 0 ? 'text-gray-400 hover:text-red-600 hover:bg-red-50' : 'text-gray-400 hover:text-red-600 hover:bg-red-50')}`}
          title={mon.is_favorite ? "Starred Pokémon cannot be released" : "Release"}
        >
          {tier === 0 ? <Trash2 size={12}/> : <Trash2 size={16}/>}
        </button>
      </div>

    </div>
  );
}